import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Whether the user has asked their OS to reduce motion ("Reduce motion" in the
// Windows / macOS / iOS accessibility settings).
//
// Needed in JS, not just CSS, because of scrolling: `scrollTo({ behavior:
// "smooth" })` requests a smooth scroll unconditionally, and a CSS
// `scroll-behavior: auto` does NOT override an explicit behavior option -- the
// CSS value is only consulted when behavior is "auto". So the caller has to
// pick (MA-213).
//
// Read per call rather than cached: the setting can change while the tab is
// open, and this runs on a user action, not in a hot loop. Guarded for
// environments without matchMedia (jsdom does not implement it) -- unknown means
// "the user has not asked for less motion", i.e. keep the default behaviour.
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Backend session timestamps may arrive without a timezone designator (naive
// UTC written by the backend). `new Date("2026-07-20T18:46:09")` parses a
// tz-less string as *local* time, which can bucket a chat into the wrong day in
// the history sidebar. Treat a tz-less value as UTC (MA-175).
export function parseTimestampUTC(value: string): Date {
  const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTz ? value : `${value}Z`);
}
